/**
 * Notifications repository.
 *
 * Notifications are append-only; there's no `rev`/OCC and no soft delete.
 * `read_at` is patched in place via `markRead` (not OCC-protected — single
 * client per session, last-write-wins is fine).
 *
 * The append/list/markRead methods take a `RepoCtx` ({ client, spreadsheetId })
 * because callers from `occ.ts` and `sync-queue.ts` already hold that context;
 * threading two positional args through every call gets noisy and obscures
 * what the test mocks are asserting on (the input shape, not the wiring).
 */

import type { SheetsClient } from '../client';
import { TABS, tabRange } from '../schema';
import { nowIso, objectToRow, rowToObject, tabRowRange } from '../row-utils';
import type { NotificationKind, NotificationRow } from '../types';

export interface RepoCtx {
  client: SheetsClient;
  spreadsheetId: string;
}

export interface AppendNotificationInput {
  kind: NotificationKind;
  payload: Record<string, unknown>;
  /** Optional pre-generated id. When supplied, the sheet row uses it
   *  verbatim instead of generating a fresh UUID — letting the caller
   *  do an optimistic local insert under the same id, so reconciliation
   *  on the next reload doesn't surface a duplicate notification. */
  id?: string;
  /** Optional pre-generated `created_at`. Same rationale as `id` —
   *  keeps optimistic local entry and sheet row byte-identical. */
  created_at?: string;
}

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function parse(row: readonly string[]): NotificationRow {
  const o = rowToObject(TABS.notifications.headers, row);
  return {
    id: o['id'] ?? '',
    kind: ((o['kind'] ?? 'system') as NotificationKind),
    payload: o['payload'] ?? '',
    read_at: (o['read_at'] ?? '') as NotificationRow['read_at'],
    created_at: o['created_at'] ?? '',
  };
}

export const notificationsRepo = {
  async append(ctx: RepoCtx, input: AppendNotificationInput): Promise<NotificationRow> {
    const row: NotificationRow = {
      id: input.id ?? genId(),
      kind: input.kind,
      payload: JSON.stringify(input.payload ?? {}),
      read_at: '',
      created_at: input.created_at ?? nowIso(),
    };
    const values = objectToRow(TABS.notifications.headers, {
      id: row.id,
      kind: row.kind,
      payload: row.payload,
      read_at: row.read_at,
      created_at: row.created_at,
    });
    await ctx.client.valuesAppend(ctx.spreadsheetId, `${TABS.notifications.name}!A1`, [values]);
    return row;
  },

  async list(ctx: RepoCtx): Promise<NotificationRow[]> {
    const resp = await ctx.client.valuesGet(
      ctx.spreadsheetId,
      tabRange('notifications', { startRow: 2 }),
    );
    const rows = (resp.values ?? []) as string[][];
    return rows.filter((r) => (r[0] ?? '') !== '').map(parse);
  },

  async markRead(ctx: RepoCtx, id: string): Promise<void> {
    const headers = TABS.notifications.headers;
    const resp = await ctx.client.valuesGet(
      ctx.spreadsheetId,
      tabRange('notifications', { startRow: 2 }),
    );
    const rows = (resp.values ?? []) as string[][];
    const idIdx = headers.indexOf('id');
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] ?? [];
      if ((r[idIdx] ?? '') === id) {
        const merged = rowToObject(headers, r);
        merged['read_at'] = nowIso();
        await ctx.client.valuesUpdate(
          ctx.spreadsheetId,
          tabRowRange('notifications', i + 2),
          [objectToRow(headers, merged)],
        );
        return;
      }
    }
  },
};
